import { applyClaims, User } from '@actiris/bff';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AppPermissions } from '@core/bff/app-permissions';
import { applyPermissions } from '@core/helpers/apply-permissions';
import { LoginRequest } from '@core/models/api/authentication/login-request';
import { LogoutResponse } from '@core/models/api/authentication/logout-response';
import { ProfileRequest } from '@core/models/api/authentication/profile-request';
import { Language } from '@core/models/language';
import { catchError, first, map, of, Subject, switchMap, tap } from 'rxjs';
import { AuthenticationApiService } from './api/authentication-api.service';
import { MyAccountApiService } from './api/my-account-api.service';

type AuthenticationState = {
    isInitialized: boolean;
    isLoggedIn: boolean;
    user: User | null;
    permissions: AppPermissions | null;
};
@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private authenticationApi = inject(AuthenticationApiService);
    private myAccountApi = inject(MyAccountApiService);

    private state = signal<AuthenticationState>({
        isInitialized: false,
        isLoggedIn: false,
        user: null,
        permissions: null,
    });

    // Selectors
    isInitialized = computed(() => this.state().isInitialized);
    isInitialized$ = toObservable(this.isInitialized);
    isLoggedIn = computed(() => this.state().isLoggedIn);
    isLoggedIn$ = toObservable(this.isLoggedIn);
    user = computed(() => this.state().user);
    user$ = toObservable(this.user);
    permissions = computed(() => this.state().permissions);
    permissions$ = toObservable(this.permissions);

    private getUserSubject = new Subject<void>();
    private getPermissionsSubject = new Subject<void>();

    private _bypassAutomaticLogout = false;
    public get bypassAutomaticLogout(): boolean {
        try {
            return this._bypassAutomaticLogout;
        } finally {
            this._bypassAutomaticLogout = false;
        }
    }

    public set bypassAutomaticLogout(value: boolean) {
        this._bypassAutomaticLogout = value;
    }

    constructor() {
        this.setupUpdateUser();
        this.setupUpdatePermissions();
    }

    // Actions

    fetchUser() {
        this.getUserSubject.next();
    }

    fetchPermissions() {
        this.getPermissionsSubject.next();
    }

    profiles(request: ProfileRequest, language: Language) {
        return this.authenticationApi.profiles(request, language);
    }

    login(request: LoginRequest) {
        return this.authenticationApi.login(request).pipe(
            tap(() => {
                this.fetchUser();
            }),
            switchMap((response) =>
                // make sure that the user is fetched before returning the response
                this.user$.pipe(
                    first((user) => !!user),
                    map(() => response),
                ),
            ),
        );
    }

    context(returnUrl: string) {
        return this.authenticationApi.context(returnUrl);
    }

    logout(logoutId: string | null) {
        const logoutInfo$ = logoutId
            ? this.authenticationApi.logoutInfo(logoutId)
            : of<LogoutResponse>({ prompt: true, postLogoutRedirectUri: '', iFrameUrl: '', externalLogoutUrl: '' });

        return logoutInfo$.pipe(
            switchMap((info) => {
                if (info.prompt) {
                    return this.authenticationApi.logout(logoutId).pipe(map((logoutResponse) => ({ ...info, ...logoutResponse })));
                }
                return of(info);
            }),
            tap(() => this.setUser(null)),
        );
    }

    private setupUpdateUser() {
        this.getUserSubject
            .pipe(
                switchMap(() => this.authenticationApi.user().pipe(catchError(() => of(null)))),
                map((user) => (user ? applyClaims(user, new User()) : null)),
                tap((user) => {
                    if (user) {
                        this.fetchPermissions();
                    }
                }),
            )
            .subscribe((user) => {
                this.setUser(user);
            });
    }

    private setupUpdatePermissions() {
        this.getPermissionsSubject
            .pipe(
                switchMap(() => this.myAccountApi.permissions()),
                map((permissions) => applyPermissions(permissions, new AppPermissions())),
            )
            .subscribe((permissions) => this.setPermissions(permissions));
    }

    private setUser(user: User | null) {
        this.state.update((state) => ({ ...state, user, isLoggedIn: !!user, isInitialized: true }));
    }

    private setPermissions(permissions: AppPermissions) {
        this.state.update((state) => ({ ...state, permissions }));
    }
}
